import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { Id } from "../../../api/types";
import { activeRoles } from "../../../app/access";
import { useCurrentMe } from "../../../app/auth";
import { useLive } from "../../../live/store";
import { Banner, Button, Card, EmptyState, Loading } from "../../../ui";
import { useArrivals } from "../hooks";
import { useOpenSet } from "../useOpenSet";
import { highlightFor, idsToOpen, NO_FILTERS, type NavFilters } from "../model/filters";
import { ancestorsOf, buildNavModel } from "../model/nav";
import { countsAt, orderedFrames, peopleIn, replayModel, stepsOf, touchedBy } from "../model/replay";
import { CounterRow, liveCounterItems, replayCounterItems } from "../parts/CounterRow";
import { FilterBar } from "../parts/FilterBar";
import { NavigatorGraph } from "../parts/NavigatorGraph";
import { NavigatorList } from "../parts/NavigatorList";
import { NodeDetail } from "../parts/NodeDetail";
import { ReplayBar } from "../parts/ReplayBar";
import { ViewSwitch } from "../parts/ViewSwitch";

type Mode = "live" | "navigate";

/** A phone-sized screen: the list is easier to use there than a map. */
function onNarrowScreen(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 640px)").matches;
}

/**
 * The family navigator (screen 5, DEC-1016-51) and the live view with the replay slider (DEC-1016-35). The same
 * tree is drawn as a map and as a list, with filters by state and by person and the counters. In the live view
 * the slider steps back through the recorded changes; the tree, the states and the counters are those of the chosen
 * moment. Nothing is replayed on the server: the browser steps through the history feed.
 */
export function LiveViewPage({ mode }: { mode: Mode }) {
  const me = useCurrentMe();
  const tree = useApiQuery("family_tree", {});
  const feed = useApiQuery("history_feed", {});
  const counters = useApiQuery("counters", {});
  const live = useLive();
  const [params, setParams] = useSearchParams();
  const selected = params.get("select");
  const [filters, setFilters] = useState<NavFilters>(NO_FILTERS);
  const [cutoff, setCutoff] = useState<number | null>(null);
  const [view, setView] = useState<"map" | "list">(() => (mode === "live" && !onNarrowScreen() ? "map" : "list"));
  const frame = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);

  const liveModel = useMemo(() => buildNavModel(tree.data), [tree.data]);
  const frames = useMemo(() => orderedFrames(feed.data), [feed.data]);
  const steps = useMemo(() => stepsOf(frames), [frames]);
  // The replay slider belongs to the family navigator, the main live view, as well as to the live view (DEC-1016-35, UI-20).
  const replaying = cutoff !== null;
  const model = useMemo(() => (replaying ? replayModel(frames, { event: cutoff }, liveModel) : liveModel), [replaying, frames, cutoff, liveModel]);
  const people = useMemo(() => peopleIn(frames), [frames]);
  const { expanded, toggle, open } = useOpenSet(model, selected);

  const cut = useMemo(() => (replaying ? { event: cutoff } : null), [replaying, cutoff]);
  const touched = useMemo(() => (filters.person === "all" ? null : touchedBy(frames, filters.person, cut)), [frames, filters.person, cut]);
  const highlight = useMemo(() => highlightFor(model, filters, touched), [model, filters, touched]);

  const nodeIds = useMemo(() => model.nodes.map((node) => node.id), [model]);
  const arrivals = useArrivals(nodeIds);

  useEffect(() => {
    const onChange = () => setFull(document.fullscreenElement === frame.current && !!frame.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const canFull = typeof document !== "undefined" && document.fullscreenEnabled === true;
  const toggleFull = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void frame.current?.requestFullscreen();
  };

  const choose = (id: Id | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("select", id);
    else next.delete("select");
    setParams(next, { replace: true });
    if (id) open(new Set(ancestorsOf(model, id).map((ancestor) => ancestor.id)));
  };

  const changeFilters = (next: NavFilters) => {
    setFilters(next);
    const nextTouched = next.person === "all" ? null : touchedBy(frames, next.person, cut);
    open(idsToOpen(model, highlightFor(model, next, nextTouched)));
  };

  const counterItems = replaying
    ? replayCounterItems(countsAt(frames, cut))
    : counters.data
      ? liveCounterItems(counters.data)
      : live.counts
        ? liveCounterItems(live.counts.counters)
        : null;

  const title = mode === "live" ? "Live view" : "Family navigator";
  const canStart = activeRoles(me).includes("engineer");
  const summary = highlight ? (highlight.match.size === 0 ? "Nothing matches these filters." : `${highlight.match.size} of ${model.nodes.length} match.`) : undefined;
  const selectedNode = selected ? model.byId.get(selected) : undefined;

  let body;
  if (tree.isPending) {
    body = <Loading>Reading the family tree</Loading>;
  } else if (tree.isError) {
    body = (
      <Card>
        <EmptyState title="The family tree could not be read" action={<Button onClick={() => void tree.refetch()}>Try again</Button>}>
          {refusalText(tree.error)}
        </EmptyState>
      </Card>
    );
  } else if (model.nodes.length === 0) {
    body = (
      <Card>
        <EmptyState title={replaying ? "Nothing existed yet at this point" : "The family tree is empty"}>
          {replaying ? "Move the slider forward to see the tree grow." : "Libraries, families and items appear here as soon as your team adds them. Moving around starts with the family tree."}
        </EmptyState>
      </Card>
    );
  } else {
    body = (
      <div className="nv-live__body">
        <Card className="nv-live__main">
          <div className="nv-live__switch row" role="group" aria-label="How to draw the tree">
            <Button variant={view === "map" ? "primary" : "secondary"} aria-pressed={view === "map"} onClick={() => setView("map")}>
              Map
            </Button>
            <Button variant={view === "list" ? "primary" : "secondary"} aria-pressed={view === "list"} onClick={() => setView("list")}>
              List
            </Button>
          </div>
          {view === "map" ? (
            <NavigatorGraph model={model} expanded={expanded} highlight={highlight} selectedId={selected} onSelect={choose} onToggle={toggle} />
          ) : (
            <NavigatorList model={model} expanded={expanded} onToggle={toggle} label="Family tree" selectedId={selected} onSelect={choose} highlight={highlight} arrivals={arrivals} />
          )}
        </Card>
        <aside className="nv-live__side" aria-label="The chosen node">
          {selected && selectedNode ? (
            <NodeDetail key={selected} model={model} id={selected} expanded={expanded} onToggle={toggle} canStartSession={canStart} replaying={replaying} />
          ) : selected ? (
            <Card>
              <EmptyState title="Not part of the family tree">
                {replaying ? "It did not exist yet at this point of the replay, or it is not a library, family or item." : "It is not a library, a family or an item, so it has no place in the tree."}{" "}
                <Link to={`/elements/${encodeURIComponent(selected)}`}>Open its page</Link>
              </EmptyState>
            </Card>
          ) : (
            <Card>
              <p className="muted">Choose a name in the tree to see where it sits, how far it is, and to open its map or its page.</p>
            </Card>
          )}
        </aside>
      </div>
    );
  }

  return (
    <div className="nv-live" ref={frame} data-full={full || undefined}>
      <header className="nv-page__head">
        <h1>{title}</h1>
        <ViewSwitch />
        {canFull && (
          <Button variant="quiet" onClick={toggleFull}>
            {full ? "Leave full screen" : "Full screen"}
          </Button>
        )}
      </header>
      <p className="muted">
        {mode === "live"
          ? "The whole discipline as your colleagues work. New elements pop in and states change colour as they happen."
          : "Move around by family: library, family, sub-family, parent item and item. The slider steps back through how the ontology grew."}
      </p>
      {counterItems && <CounterRow items={counterItems} label={replaying ? "Counters at the chosen moment" : "Counters"} />}
      <FilterBar filters={filters} onChange={changeFilters} people={people} summary={summary} />
      {feed.isError ? (
        <Banner>The replay is not available right now. The tree above is the present state.</Banner>
      ) : feed.isPending ? null : (
        <ReplayBar steps={steps} markers={feed.data?.markers ?? []} cutoff={cutoff} onChange={setCutoff} />
      )}
      {body}
    </div>
  );
}
